
import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders } from '../../utils/api';
import '../../styles/common.css';
import '../placement/TopRecruiters.css';
import './ManetBlogs.css';

const ManetBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState({ title: '', blog_by: '', blog_date: '', blog_image: null, message: '' });
  const [editingId, setEditingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userRole, setUserRole] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token', e);
      }
    }
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/manet-blogs`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      setBlogs(data.data || []);
    } catch (err) { setError(err.message || 'Failed'); }
    finally { setLoading(false); }
  }

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      setError('Image must be less than 500KB. Please compress the image and try again.');
      e.target.value = '';
      return;
    }
    setRemoveImage(false);
    setForm(prev => ({ ...prev, blog_image: file }));
    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);
  }

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/manet-blogs/${id}/approve`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      setSuccess('Blog approved successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchBlogs();
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  }

  const handleReject = async (id) => {
    if (!window.confirm('Are you sure you want to reject this blog?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/manet-blogs/${id}/reject`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      setSuccess('Blog rejected successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchBlogs();
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  }

  const resetForm = () => {
    setForm({ title: '', blog_by: '', blog_date: '', blog_image: null, message: '' });
    setEditingId(null); setPreviewImage(null); setRemoveImage(false); setError(''); setSuccess('');
  }

  const handleSubmit = async e => {
    e.preventDefault(); setError(''); setSuccess('');

    if (!form.title || !form.blog_by) {
      setError('Title and Blog By are required');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const formData = new FormData();
    formData.append('title', form.title);
    formData.append('blog_by', form.blog_by);
    if (form.blog_date) formData.append('blog_date', form.blog_date);
    formData.append('message', form.message || '');
    if (removeImage) formData.append('remove_image', 'true');
    if (form.blog_image) formData.append('blog_image', form.blog_image);

    try {
      const url = editingId ? `${API_BASE}/api/admin/manet-blogs/${editingId}` : `${API_BASE}/api/admin/manet-blogs`;
      const method = editingId ? 'PUT' : 'POST';
      const headers = { Authorization: authHeaders().Authorization };
      const res = await fetch(url, { method, headers, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      setSuccess(data.message || (editingId ? 'Blog updated successfully' : 'Blog created successfully'));
      setTimeout(() => setSuccess(''), 3000);
      resetForm();
      window.location.reload();
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  }

  const handleEdit = blog => {
    setForm({ title: blog.title, blog_by: blog.blog_by, blog_date: blog.blog_date?.slice(0, 10), blog_image: null, message: blog.message || '' });
    setEditingId(blog.id);
    setPreviewImage(blog.blog_image ? `${API_BASE}${blog.blog_image}` : null);
  }

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this blog? This action cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/manet-blogs/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      setSuccess('Blog deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchBlogs();
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  }

  return (
    <div className="admin-page top-recruiters-page">
      <div className="page-heading">
        <h1>Manage Blogs</h1>
        <p>Create and manage blog posts for the website.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      <div className="recruiters-grid">
        <div className="card">
          <h2>{editingId ? 'Edit Blog' : 'Add Blog'}</h2>
          <form className="recruiter-form" onSubmit={handleSubmit}>
            <label>
              Title *
              <input name="title" value={form.title} onChange={handleChange} required />
            </label>
            <label>
              Blog By *
              <input name="blog_by" value={form.blog_by} onChange={handleChange} required />
            </label>
            <label>
              Date
              <input type="date" name="blog_date" value={form.blog_date} onChange={handleChange} />
            </label>
            <label>
              Message
              <textarea name="message" value={form.message} onChange={handleChange} rows="4" />
            </label>
            <label>
              Image <small style={{color:'#888'}}>(max 500KB)</small>
              <input type="file" accept="image/*" onChange={handleFileChange} />
              {previewImage && !removeImage && (
                <div className="image-preview-item">
                  <img src={previewImage} alt="Preview" />
                  {editingId && (
                    <button
                      type="button"
                      className="btn-delete"
                      style={{ marginTop: '8px' }}
                      onClick={() => { setRemoveImage(true); setPreviewImage(null); setForm(prev => ({ ...prev, blog_image: null })); }}
                    >
                      🗑️ Remove Photo
                    </button>
                  )}
                </div>
              )}
              {removeImage && <p style={{ color: '#e53e3e', fontSize: '13px' }}>Photo will be removed on save.</p>}
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              {editingId && (
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2>Blog List</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}>
              <option value="all">All Blogs</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : blogs.filter(blog => statusFilter === 'all' || blog.status === statusFilter).length === 0 ? (
            <p>No blogs found.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.filter(blog => statusFilter === 'all' || blog.status === statusFilter).map(blog => (
                  <tr key={blog.id}>
                    <td>{blog.title}</td>
                    <td>{blog.blog_by}</td>
                    <td>{blog.blog_date ? new Date(blog.blog_date).toLocaleDateString() : '—'}</td>
                    <td><span className={`status status-${blog.status}`}>{blog.status}</span></td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(blog)}>✏️ Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(blog.id)}>🗑️ Delete</button>
                      {blog.status !== 'approved' && userRole === 'superadmin' && <button className="btn-edit" onClick={() => handleApprove(blog.id)}>✅ Approve</button>}
                      {blog.status !== 'rejected' && blog.status !== 'approved' && userRole === 'superadmin' && <button className="btn-delete" onClick={() => handleReject(blog.id)}>❌ Reject</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManetBlogs;
